import type { FeatureEvents, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

type ScreenKeys = 'automaticAiAccessTabScreen' | 'addGroupsToGrantAiAccessModalScreen' | 'confirmationWarningModalScreen';
type UiKeys =
  | 'automaticAiAccessToggle'
  | 'addGroupsToAiAccessClick'
  | 'cancelAddGroupsToAiAccessClick'
  | 'addGroupsClick'
  | 'removeAiAccessConfirmClick'
  | 'enableAutomaticAiAccessConfirmClick'
  | 'removeAiAccessCancelClick'
  | 'enableAutomaticAiAccessCancelClick';
type TrackKeys = 'assignAiAccess' | 'removeAiAccess' | 'enableAutomaticAiAccess' | 'disableAutomaticAiAccess';

const automaticAiAccessTabScreen = 'automaticAiAccessTabScreen';
const addGroupsToGrantAiAccessModalScreen = 'addGroupsToGrantAiAccessModalScreen';
const confirmationWarningModalScreen = 'confirmationWarningModalScreen';

export const automaticAiAccessTabEvents: FeatureEvents<ScreenKeys, UiKeys, TrackKeys, string> = {
  confluenceSpec: ['https://hello.atlassian.net/wiki/spaces/CLOUDADMIN/pages/5684875982/AI-RBAC+Analytics+Spec+adminhub-scope'],
  screen: {
    automaticAiAccessTabScreen: (): ScreenEvent => ({
      data: {
        name: automaticAiAccessTabScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90624',
    }),
    addGroupsToGrantAiAccessModalScreen: (): ScreenEvent => ({
      data: {
        name: addGroupsToGrantAiAccessModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90625',
    }),
    confirmationWarningModalScreen: (attributes: { modalName: string }): ScreenEvent => ({
      data: {
        name: confirmationWarningModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90711',
    }),
  },
  ui: {
    automaticAiAccessToggle: (attributes: { enabled: boolean }): UIEvent => ({
      data: {
        action: Action.Toggled,
        actionSubject: ActionSubject.Toggle,
        actionSubjectId: 'automaticAiAccessToggle',
        source: automaticAiAccessTabScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90685',
    }),
    addGroupsToAiAccessClick: (attributes: { groupsCount: number; groupIds: string[] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addGroupsToAiAccessClick',
        source: addGroupsToGrantAiAccessModalScreen,
        attributes: {
          groupsCount: attributes.groupsCount,
          groupIds: attributes.groupIds,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90675',
    }),
    cancelAddGroupsToAiAccessClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelAddGroupsToAiAccessClick',
        source: addGroupsToGrantAiAccessModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90677',
    }),
    addGroupsClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addGroupsClick',
        source: automaticAiAccessTabScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90650',
    }),
    removeAiAccessConfirmClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'removeAiAccessConfirmClick',
        source: confirmationWarningModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90678',
    }),
    enableAutomaticAiAccessConfirmClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'enableAutomaticAiAccessConfirmClick',
        source: confirmationWarningModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90862',
    }),
    removeAiAccessCancelClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'removeAiAccessCancelClick',
        source: confirmationWarningModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90682',
    }),
    enableAutomaticAiAccessCancelClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'enableAutomaticAiAccessCancelClick',
        source: confirmationWarningModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90863',
    }),
  },
  track: {
    assignAiAccess: (attributes: { groupId: string; directoryId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Added,
        actionSubject: TrackActionSubject.AssignGroupAiAccess,
        source: addGroupsToGrantAiAccessModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90700',
    }),
    removeAiAccess: (attributes: { groupId: string; directoryId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Removed,
        actionSubject: TrackActionSubject.RemoveGroupAiAccess,
        source: confirmationWarningModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90699',
    }),
    enableAutomaticAiAccess: (): TrackEvent => ({
      data: {
        action: TrackAction.Enabled,
        actionSubject: TrackActionSubject.EnableAutomaticAiAccess,
        source: confirmationWarningModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90701',
    }),
    disableAutomaticAiAccess: (): TrackEvent => ({
      data: {
        action: TrackAction.Disabled,
        actionSubject: TrackActionSubject.DisableAutomaticAiAccess,
        source: automaticAiAccessTabScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90701',
    }),
  },
  operational: {},
};
